import {
  Injectable,
  signal,
  WritableSignal,
  Signal,
  inject,
} from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, throwError, from, of } from "rxjs";
import { catchError, map, filter } from "rxjs/operators";
import {
  getAI,
  getGenerativeModel,
  GoogleAIBackend,
  GenerativeModel,
} from "firebase/ai";
import { getApp } from "firebase/app";
import { DashboardStateService } from "@app/parse-analyze-track/services/dashboard-state.service";
import { SummarizationService } from "@app/parse-analyze-track/services/summarization.service";
import { Router, NavigationEnd, Event as RouterEvent } from "@angular/router";
import { GridItemType } from "../types/platform.types";

/**
 * Supported LLM providers
 */
export enum LLMProvider {
  GEMINI = "gemini",
  OPENAI = "openai",
}

/**
 * Base interface for LLM configuration
 */
export interface LLMConfig {
  provider: LLMProvider;
  apiKey?: string;
  modelName: string;
  temperature?: number;
  maxTokens?: number;
}

/**
 * Interface for dashboard context data passed to the LLM
 */
export interface DashboardContextData {
  [metricName: string]: any; // Allows flexible structure for various metrics
  timestamp?: string; // Optional: to know when the context was generated
  source?: string; // Optional: to identify the chart/dashboard explicitly
}

/**
 * OpenAI specific response structure for completions
 */
interface OpenAIChoice {
  text: string;
  index: number;
  logprobs: any | null;
  finish_reason: string;
}

interface OpenAICompletionsResponse {
  id: string;
  object: string;
  created: number;
  model: string;
  choices: OpenAIChoice[];
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

/**
 * Response format from LLM providers (internal, not directly used by DashboardContextData)
 */
interface LLMResponse {
  text: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

/**
 * Service for interacting with various LLM providers
 */
@Injectable({
  providedIn: "root",
})
export class AIService {
  // Default configuration
  private config: LLMConfig = {
    provider: LLMProvider.GEMINI,
    modelName: "gemini-2.5-flash-preview-05-20",
    temperature: 0.2,
    maxTokens: 10000,
  };

  // Firebase AI service and model
  private firebaseAI: any;
  private generativeModel: GenerativeModel | null = null;

  // Store for dashboard metrics
  // Key: A unique identifier for the dashboard/chart (e.g., 'mainDashboard_overallStats')
  // Value: A signal holding the metrics for that dashboard/chart
  private dashboardMetrics = new Map<
    string,
    WritableSignal<DashboardContextData | null>
  >();

  // Inject new services
  private dashboardStateService = inject(DashboardStateService);
  private summarizationService = inject(SummarizationService);
  private router = inject(Router);
  private currentRoutePath = signal<string>("");

  constructor(private http: HttpClient) {
    this.initializeFirebaseAI();

    // Subscribe to router events to get the current path
    this.router.events
      .pipe(
        filter(
          (event: RouterEvent): event is NavigationEnd =>
            event instanceof NavigationEnd
        )
      )
      .subscribe((event: NavigationEnd) => {
        this.currentRoutePath.set(event.urlAfterRedirects || event.url);
        // console.log("Current route path set to:", this.currentRoutePath());
      });
  }

  private initializeFirebaseAI(): void {
    try {
      const firebaseApp = getApp();
      this.firebaseAI = getAI(firebaseApp, { backend: new GoogleAIBackend() });
      // console.log("Firebase AI service initialized successfully");
    } catch (error) {
      console.error("Error initializing Firebase AI service:", error);
    }
  }

  private getGenerativeModel(): GenerativeModel {
    if (!this.generativeModel) {
      if (!this.firebaseAI) {
        throw new Error("Firebase AI service not initialized");
      }
      this.generativeModel = getGenerativeModel(this.firebaseAI, {
        model: this.config.modelName,
        generationConfig: {
          temperature: this.config.temperature,
          maxOutputTokens: this.config.maxTokens,
          topP: 0.8,
          topK: 40,
        },
      });
    }
    return this.generativeModel;
  }

  setConfig(config: Partial<LLMConfig>): void {
    this.config = { ...this.config, ...config };
    if (config.provider || config.modelName || config.apiKey) {
      this.initializeFirebaseAI();
    }
  }

  getConfig(): LLMConfig {
    return { ...this.config };
  }

  /**
   * Updates or sets the metrics for a specific dashboard/chart.
   * @param dashboardId A unique identifier for the dashboard or chart.
   * @param metrics The metrics data to store.
   */
  updateDashboardContext(
    dashboardId: string,
    metrics: DashboardContextData
  ): void {
    const currentTimestamp = new Date().toISOString();
    const metricsWithDetails: DashboardContextData = {
      ...metrics,
      timestamp: currentTimestamp,
      source: dashboardId,
    };

    if (this.dashboardMetrics.has(dashboardId)) {
      this.dashboardMetrics.get(dashboardId)!.set(metricsWithDetails);
    } else {
      this.dashboardMetrics.set(dashboardId, signal(metricsWithDetails));
    }
    this.dashboardStateService.updateGridData(dashboardId, metricsWithDetails);
  }

  /**
   * Retrieves the metrics signal for a specific dashboard/chart.
   * @param dashboardId The unique identifier for the dashboard or chart.
   * @returns A signal containing the metrics, or undefined if not found.
   */
  getDashboardContextSignal(
    dashboardId: string
  ): Signal<DashboardContextData | null> | undefined {
    return this.dashboardMetrics.get(dashboardId);
  }

  /**
   * Retrieves the current metrics data for a specific dashboard/chart.
   * @param dashboardId The unique identifier for the dashboard or chart.
   * @returns The metrics data, or null if not found.
   */
  getCurrentDashboardContext(dashboardId: string): DashboardContextData | null {
    const metricSignal = this.dashboardMetrics.get(dashboardId);
    return metricSignal ? metricSignal() : null;
  }

  testFirebaseAI(): Observable<string> {
    // console.log("Testing Firebase AI integration...");
    return this.sendMessage(
      "Give me a quick 2-sentence summary about stock trading."
    );
  }

  sendMessage(message: string): Observable<string> {
    if (!this.config.apiKey && this.config.provider === LLMProvider.OPENAI) {
      return throwError(
        () => new Error("OpenAI API key not set. Please configure the service.")
      );
    }
    // if (!this.generativeModel && this.config.provider === LLMProvider.GEMINI) {
    //   this.initializeFirebaseAI();
    //   if (!this.generativeModel) {
    //     return throwError(
    //       () => new Error("Firebase Generative model not initialized")
    //     );
    //   }
    // }

    let finalPrompt = "";
    const route = this.currentRoutePath();
    let llmContextString = "";

    if (
      route.startsWith("/parse-analyze-track/dashboard") ||
      route.startsWith("/parse-analyze-track/history") ||
      route.startsWith("/parse-analyze-track/positions")
    ) {
      const allGridData = this.dashboardStateService.getAllGridData();
      const summaries: string[] = [];
      allGridData.forEach((gridData, gridId: string) => {
        if (gridData) {
          const summary = this.summarizationService.summarize(gridId, gridData);
          if (
            summary &&
            !summary.startsWith("No summarization available") &&
            !summary.startsWith("Data is incomplete") &&
            !summary.startsWith("Error summarizing")
          ) {
            summaries.push(summary);
          }
        }
      });
      if (summaries.length > 0) {
        llmContextString =
          "Current Dashboard Overview:\n" + summaries.join("\n\n---\n\n");
      } else {
        llmContextString =
          "No dashboard data currently available for summarization.";
      }
      finalPrompt = this.buildTraderPrompt(message, llmContextString);
      // console.log("Using Trader Prompt for route:", route);
    } else if (route === "/" || route === "/home") {
      finalPrompt = this.buildHomePagePrompt(message);
      // console.log("Using Home Page Prompt for route:", route);
    } else {
      // console.log("No default prompt for route:", route);
    }

    // console.log("Final LLM Prompt being sent:", finalPrompt);

    switch (this.config.provider) {
      case LLMProvider.GEMINI:
        return this.sendToGemini(finalPrompt);
      case LLMProvider.OPENAI:
        return this.sendToOpenAI(finalPrompt);
      default:
        return throwError(
          () => new Error(`Unsupported LLM provider: ${this.config.provider}`)
        );
    }
  }

  // TODO KILLER FEATURE USERS WILL PAY UP FOR: whenever a user asks about a particular underlying (or if possible all underlyings mentioned) make the LLM go out and get
  //quote data for the data range of the trades so it can make comparisons to the underlying performance for the given time frame with the user's trading performance
  //not sure the best way to do this: MCP?  or just an API?  use schwab API initially?
  private buildTraderPrompt(message: string, context?: string): string {
    let prompt = `
You are an expert stock and options trader with over 20 years of experience in all market conditions.
You've successfully navigated market crashes with extreme volatility, slow sideways markets, and everything in between.
You're well-versed in both long-term and short-term trading strategies, as well as simple and complex options strategies including:
- Covered calls and protective puts
- Vertical spreads (bull/bear)
- Iron condors and butterflies
- Calendar and diagonal spreads
- Ratio spreads and backspreads
- Advanced volatility strategies
- Weekly options, especially with SPX/SPXW daily (0DTE) strategies

You understand technical analysis, fundamental analysis, and market psychology.
You can explain complex trading concepts in clear, actionable terms.
You're helping a trader analyze their data in the ReTraid application. Always spell the product name ReTraid (capital R and T), pronounced ree-TRAYD.

********
IMPORTANT: Whenever user asks you a question, every answer you give should be educational and helpful in a way
that is not just repeating the data in your context back to the user.  example: if a user asks "how did I do today?"
Don't just list the trades and the PL back to the user.  They user can see this themselves in the dashboard.  Try to
come up with insights that they may not see directly from the metrics themselves.  For example you might tell
the user their overall summary of the trades and PL, but you could also mention that their profits were signifincatly
larger than their losses, which is a good sign.  Or that even though their win rate was low, their profits were overall
larger than their losses - still a good sign.  

So bottom line: always try to provide insights that would require more thought and correlation investigation utilizing
all the provided context (dashboard metrics, trade history, notes, etc.) than just 
spitting out the numbers in the context/dashboard. You must alwyays strive to give them valuable information 
that they most likely could not do on their own or would take them a long time to figure out with given data.

If you can find an opportunity to offer some insight that is math based and not easy to spot out on your own
then do so and explain the math. For example if the win rate is high but the avg total loss is 3x higher
than the gain, bring attention to these types of insights
********

********
User's question to LLM: ${message}
********

`;

    if (context && context.trim() !== "" && context.trim() !== "{}") {
      prompt += `\nRelevant trade data context from the current dashboard view:\n${context}\n`;
    }

    return prompt;
  }

  private buildHomePagePrompt(message: string, context?: string): string {
    let prompt = `You are a helpful assistant for the ReTraid platform. Always spell the product name ReTraid (capital R and T), pronounced ree-TRAYD.
Currently, the user is on the home page.
Your primary goal is to answer questions about ReTraid, its features, and help users understand what they are seeing or how to navigate the platform.
Be friendly, concise, and informative. Do not invent features or make up information.

ReTraid Platform Overview:
- Key Features: ReTraid is an intelligent trading journal and analytics platform. Users can upload trade history (from Schwab, E-Trade, with more brokers planned) to gain insights into their trading performance.
- Interactive Dashboard: The platform offers a rich dashboard with various charts like P/L by Underlying, P/L over Time, strategy performance, and more. Users can customize views and save them.
- AI Chat Assistant (You): You are here to help! Users can ask you questions about their trade data, platform features, or general trading concepts (e.g., "What is a covered call?").
- Portfolio Management: Users can manage and track multiple trading portfolios.

Common Questions You Can Answer:
- "How do I upload my trades?" (Guide them to the upload section, mention supported brokers).
- "What can I ask the AI?" (Explain you can answer questions about their data, platform usage, and general finance topics related to their trades).
- "Tell me about the dashboard." (Briefly describe the types of analytics available).
- "Is my data secure?" (Reassure them about data security and privacy).
`;

    if (context && context.trim().length > 0) {
      prompt += `\n\nAdditional Context (Home Page Specific):\n${context}`;
    }

    prompt += `\n\nUser's message: "${message}"\n\nAssistant's response:`;
    return prompt;
  }

  private sendToGemini(prompt: string): Observable<string> {
    const model = this.getGenerativeModel();

    // console.log(
    //   `Sending prompt to ${this.config.modelName} via Firebase AI SDK with context length: ${prompt.length}`
    // );

    return from(model.generateContent(prompt)).pipe(
      map((result) => {
        if (!result || !result.response) {
          throw new Error("Empty response from Gemini API");
        }
        const response = result.response;
        return response.text().trim();
      }),
      catchError((error) => {
        console.error("Error calling Gemini API via Firebase AI SDK:", error);
        return throwError(
          () =>
            new Error(
              "Failed to get response from AI assistant: " + error.message
            )
        );
      })
    );
  }

  private sendToOpenAI(prompt: string): Observable<string> {
    if (!this.config.apiKey) {
      return throwError(
        () => new Error("OpenAI API key not set. Please configure the service.")
      );
    }

    const body = {
      model: this.config.modelName,
      prompt: prompt,
      max_tokens: this.config.maxTokens,
      temperature: this.config.temperature,
    };

    return this.http
      .post<OpenAICompletionsResponse>(
        "https://api.openai.com/v1/completions",
        body,
        {
          headers: new HttpHeaders({
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.config.apiKey}`,
          }),
        }
      )
      .pipe(
        map((response) => {
          if (response && response.choices && response.choices.length > 0) {
            const choice = response.choices[0];
            if (choice && choice.text) {
              return choice.text.trim();
            }
          }
          throw new Error(
            "Empty or invalid response structure from OpenAI API"
          );
        }),
        catchError((error) => {
          console.error("Error calling OpenAI API:", error);
          return throwError(
            () =>
              new Error(
                "Failed to get response from OpenAI API: " + error.message
              )
          );
        })
      );
  }
}
